import React from 'react';
import GetAppIcon from '@material-ui/icons/GetApp';
import { Button, CircularProgress } from '@material-ui/core';
import { useLazyQuery } from '@apollo/client';

import useReportQueryParams from 'components/ReportsModule/hooks/use-report-query-params';
import { resolveDateRange } from 'components/ReportsModule/utils/report-date-utils';
import { useTranslation } from 'react-i18next';
import { exportReport } from 'components/ReportsModule/utils/report-export-util';
import { ReportWorkbook } from 'components/ReportsModule/types';

interface Props<T> {
  workbook: ReportWorkbook<T>;
}

const ReportExportButton = <T extends object>({
  workbook
}: Props<T>): React.ReactElement<Props<T>> => {
  const [reportParams] = useReportQueryParams();
  const { t } = useTranslation('Reports');
  const { entityId, dateRange } = reportParams;
  const { dateStart, dateEnd } = resolveDateRange(dateRange);

  const [fetchExportData, { data, loading }] = useLazyQuery(workbook.query, {
    errorPolicy: 'all'
  });

  const handleExportClick = (): void => {
    fetchExportData({
      variables: {
        id: entityId,
        dateRange: { dateStart, dateEnd }
      }
    });
  };

  if (data) exportReport(workbook, data, reportParams);

  return (
    <Button
      onClick={handleExportClick}
      variant="outlined"
      color="secondary"
      disableElevation
      startIcon={loading ? <CircularProgress size={14} /> : <GetAppIcon />}
    >
      {t('Export Data')}
    </Button>
  );
};

export default ReportExportButton;
